import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useTranslation } from "react-i18next";

import { MatchReason } from "./matchOffer";

export type MatchOfferDismissMethod = "no_thanks" | "close" | "escape";

interface MatchOfferModalProps {
  reason: MatchReason;
  onClaim: () => void;
  onDismiss: (method: MatchOfferDismissMethod) => void;
}

// Layout, sizes and colours follow the PRD mock (Ethan's "Quiz Match Offer"
// artifact): a blue "You told us" panel above the offer on phones and beside
// it from ~600 px, the green savings pill, and the pricing page's gold CTA.

const OLD_PRICE = 15.99;
const NEW_PRICE = 11.99;

// Entrance timeline, in ms after the modal mounts.
const T = {
  toldUs: 160,
  offer: 300,
  price: 620,
  strike: 780,
  drop: 820,
  dropDuration: 520,
  cta: 720,
  confetti: 1260,
  shine: 1500,
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(36px) scale(0.86); }
  to { opacity: 1; transform: none; }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: none; }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-16px); }
  to { opacity: 1; transform: none; }
`;

const growX = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const badgePop = keyframes`
  0% { opacity: 0; transform: scale(0.4); }
  60% { opacity: 1; transform: scale(1.14); }
  100% { opacity: 1; transform: scale(1); }
`;

const priceLand = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.22); color: #317a40; }
  100% { transform: scale(1); }
`;

const burst = keyframes`
  0% { opacity: 0; transform: translate(-50%, -50%) rotate(0deg) scale(0.6); }
  12% { opacity: 1; }
  100% { opacity: 0; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)) scale(1); }
`;

const shine = keyframes`
  0% { transform: translateX(-120%) skewX(-20deg); }
  22%, 100% { transform: translateX(380%) skewX(-20deg); }
`;

const enter = (name: ReturnType<typeof keyframes>, delayMs: number, durationMs = 420) => css`
  animation: ${name} ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  overflow-y: auto;
  padding: 16px;
  background: rgba(0, 0, 0, 0.7);
  animation: ${fadeIn} 220ms ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Dialog = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  max-width: 720px;
  margin: auto;
  overflow: hidden;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  color: #1a1a1a;
  font-family: "Lato", system-ui, sans-serif;
  animation: ${popIn} 560ms cubic-bezier(0.34, 1.56, 0.64, 1) both;

  &:focus { outline: none; }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ToldUsPanel = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1 1 250px;
  flex-direction: column;
  justify-content: center;
  padding: 40px 28px;
  background: #e7f0ff;
`;

const ToldUsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ToldUsLabel = styled.div`
  color: #0b5bd6;
  font-family: "Oswald", "Arial Narrow", sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  ${enter(slideIn, T.toldUs)}
`;

const AnswerLine = styled.p<{ $delay: number }>`
  margin: 0;
  color: #161616;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.35;
  ${({ $delay }) => enter(slideIn, $delay, 480)}
`;

const AnswerDivider = styled.div<{ $delay: number }>`
  height: 1px;
  background: #b9d0f7;
  transform-origin: left center;
  ${({ $delay }) => enter(growX, $delay, 520)}
`;

const OfferPanel = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1 1 300px;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
  padding: 44px 30px 18px;
`;

const HeadlineGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const Pill = styled.div`
  padding: 6px 14px;
  border-radius: 999px;
  background: #e7f0ff;
  color: #0b5bd6;
  font-family: "Oswald", "Arial Narrow", sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  ${enter(riseIn, T.offer)}
`;

const Headline = styled.h2`
  margin: 0;
  color: #161616;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  ${enter(riseIn, T.offer + 80)}
`;

const Consequence = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.45;
  ${enter(riseIn, T.offer + 160)}
`;

const PriceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  ${enter(riseIn, T.price)}
`;

const SavingsPill = styled.span`
  padding: 4px 12px;
  border-radius: 100px;
  background: #e3f5ea;
  color: #317a40;
  font-size: 14px;
  font-weight: 700;
  ${enter(badgePop, T.price + 60, 460)}
`;

const OldPrice = styled.span`
  position: relative;
  color: #707070;
  font-size: 15px;

  &::after {
    content: "";
    position: absolute;
    left: -1px;
    right: -1px;
    top: 52%;
    height: 1.5px;
    background: currentColor;
    transform-origin: left center;
    ${enter(growX, T.strike, 260)}
  }
`;

const NewPrice = styled.span`
  position: relative;
  display: inline-block;
  color: #1a1a1a;
  font-size: 26px;
  font-variant-numeric: tabular-nums;
  font-weight: 900;
  animation: ${priceLand} 420ms ease-out ${T.drop + T.dropDuration - 80}ms both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PerMonth = styled.span`
  color: #707070;
  font-size: 13px;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const Confetti = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const ConfettiPiece = styled.i<{ $color: string; $round: boolean; $angle: number; $distance: number }>`
  --dx: ${({ $angle, $distance }) => Math.round(Math.cos(($angle * Math.PI) / 180) * $distance)}px;
  --dy: ${({ $angle, $distance }) => Math.round(Math.sin(($angle * Math.PI) / 180) * $distance)}px;
  --rot: ${({ $angle }) => $angle * 3}deg;
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $round }) => ($round ? "7px" : "6px")};
  height: ${({ $round }) => ($round ? "7px" : "11px")};
  border-radius: ${({ $round }) => ($round ? "50%" : "1px")};
  background: ${({ $color }) => $color};
  opacity: 0;
  animation: ${burst} 900ms cubic-bezier(0.15, 0.7, 0.35, 1) ${T.confetti}ms both;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const ClaimButton = styled.button`
  position: relative;
  width: 100%;
  min-height: 54px;
  overflow: hidden;
  border: none;
  border-radius: 8px;
  background: #dfb140;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  color: #000000;
  cursor: pointer;
  font-family: "Oswald", "Arial Narrow", sans-serif;
  font-size: 22px;
  font-weight: 600;
  text-transform: uppercase;
  ${enter(riseIn, T.cta, 460)}

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 32%;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0));
    transform: translateX(-120%) skewX(-20deg);
    animation: ${shine} 4200ms ease-in-out ${T.shine}ms infinite;
    pointer-events: none;
  }

  &:hover { filter: brightness(1.04); }
  &:active { filter: brightness(0.96); }
  &:focus-visible { outline: 3px solid rgba(11, 91, 214, 0.5); outline-offset: 3px; }

  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; display: none; }
  }
`;

const NoThanksButton = styled.button`
  min-height: 44px;
  border: none;
  background: transparent;
  color: #707070;
  cursor: pointer;
  font-family: "Lato", system-ui, sans-serif;
  font-size: 14px;
  ${enter(fadeIn, T.cta + 160)}

  &:focus-visible { outline: 3px solid rgba(11, 91, 214, 0.5); outline-offset: 2px; }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  ${enter(fadeIn, T.toldUs)}

  &:focus-visible { outline: 3px solid rgba(11, 91, 214, 0.5); outline-offset: -2px; }
`;

const CONFETTI = [
  { angle: -160, distance: 70, color: "#dfb140", round: false },
  { angle: -135, distance: 88, color: "#0b6dff", round: true },
  { angle: -112, distance: 64, color: "#317a40", round: false },
  { angle: -95, distance: 92, color: "#ff8a5c", round: false },
  { angle: -78, distance: 70, color: "#dfb140", round: true },
  { angle: -58, distance: 86, color: "#0b6dff", round: false },
  { angle: -36, distance: 66, color: "#317a40", round: true },
  { angle: -14, distance: 84, color: "#ff8a5c", round: false },
  { angle: 8, distance: 62, color: "#dfb140", round: false },
  { angle: 28, distance: 74, color: "#0b6dff", round: true },
  { angle: 160, distance: 66, color: "#317a40", round: false },
  { angle: 182, distance: 80, color: "#ff8a5c", round: true },
  { angle: -178, distance: 90, color: "#0b6dff", round: false },
  { angle: 140, distance: 58, color: "#dfb140", round: true },
];

const focusableSelector = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Counts the shown price down from the old price to the match price once the
// strike-through has drawn. Reduced motion shows the final price at once.
const usePriceDrop = () => {
  const [price, setPrice] = useState(() => (prefersReducedMotion() ? NEW_PRICE : OLD_PRICE));

  useEffect(() => {
    if (prefersReducedMotion()) {
      setPrice(NEW_PRICE);
      return;
    }
    let frame = 0;
    const start = performance.now() + T.drop;
    const tick = (now: number) => {
      const progress = Math.min(1, Math.max(0, (now - start) / T.dropDuration));
      const eased = 1 - Math.pow(1 - progress, 3);
      setPrice(OLD_PRICE + (NEW_PRICE - OLD_PRICE) * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  return price;
};

export const MatchOfferModal = ({ reason, onClaim, onDismiss }: MatchOfferModalProps) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const dismissRef = useRef(onDismiss);
  const shownPrice = usePriceDrop();

  useEffect(() => {
    dismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Focus the dialog itself so no button shows a focus ring on arrival;
    // the first Tab lands on Claim.
    dialogRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        dismissRef.current("escape");
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((element) => !element.hasAttribute("disabled"));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      // Safari may skip buttons in its native tab order, depending on macOS
      // keyboard settings. Cycle explicitly so focus stays inside in either mode.
      event.preventDefault();
      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      const nextIndex = currentIndex < 0
        ? (event.shiftKey ? focusable.length - 1 : 0)
        : (currentIndex + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length;
      focusable[nextIndex].focus();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  const answerLines = [reason.line1, reason.line2].filter((line): line is string => !!line);

  return (
    <Overlay>
      <Dialog
        aria-describedby="match-offer-description"
        aria-labelledby="match-offer-title"
        aria-modal="true"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <ToldUsPanel>
          <ToldUsList>
            <ToldUsLabel>{t("results.matchOffer.modal.toldUs")}</ToldUsLabel>
            {answerLines.map((line, index) => (
              <React.Fragment key={line}>
                {index > 0 && <AnswerDivider $delay={T.toldUs + 80 + index * 140} />}
                <AnswerLine $delay={T.toldUs + 80 + index * 160}>{line}</AnswerLine>
              </React.Fragment>
            ))}
          </ToldUsList>
        </ToldUsPanel>
        <OfferPanel>
          <HeadlineGroup>
            <Pill>{t("results.matchOffer.modal.pill")}</Pill>
            <Headline id="match-offer-title">{reason.headline}</Headline>
          </HeadlineGroup>
          <Consequence id="match-offer-description">{reason.consequence}</Consequence>
          <PriceRow>
            <SavingsPill>{t("results.matchOffer.modal.savings")}</SavingsPill>
            <OldPrice>${OLD_PRICE.toFixed(2)}</OldPrice>
            <NewPrice>
              <span aria-hidden="true">${shownPrice.toFixed(2)}</span>
              <VisuallyHidden>${NEW_PRICE.toFixed(2)}</VisuallyHidden>
              <Confetti aria-hidden="true">
                {CONFETTI.map(({ angle, distance, color, round }) => (
                  <ConfettiPiece key={angle} $angle={angle} $distance={distance} $color={color} $round={round} />
                ))}
              </Confetti>
            </NewPrice>
            <PerMonth>{t("results.matchOffer.modal.perMonth")}</PerMonth>
          </PriceRow>
          <Actions>
            <ClaimButton type="button" onClick={onClaim}>
              {t("results.matchOffer.modal.claim")}
            </ClaimButton>
            <NoThanksButton type="button" onClick={() => onDismiss("no_thanks")}>
              {t("results.matchOffer.modal.noThanks")}
            </NoThanksButton>
          </Actions>
        </OfferPanel>
        <CloseButton type="button" aria-label={t("results.matchOffer.modal.close")} onClick={() => onDismiss("close")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#707070" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </CloseButton>
      </Dialog>
    </Overlay>
  );
};

export default MatchOfferModal;
